import React, {createContext, useContext, useEffect, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {useAuthUser} from '../../@crema/utility/AuthHooks';

export type Setting = {
  id: string;
};

export type UserSetting = {
  id: string;
};

type SettingContextType = {
  settings: Setting[];
  userSettings: Setting[];
  loading: boolean;
};

const defaultSettingContext = {
  settings: [],
  userSettings: [],
  loading: false,
};

interface SettingActionsContextType {
  makeApiKey: Function;
  updateSetting: Function;
  fetchSettings: Function;
}

const SettingContext = createContext<SettingContextType>(defaultSettingContext);

const SettingActionsContext = createContext<SettingActionsContextType>({
  makeApiKey: () => {},
  updateSetting: () => {},
  fetchSettings: () => {},
});

export const useSettingContext = () => useContext(SettingContext);
export const useSettingActionsContext = () => useContext(SettingActionsContext);

const SettingProvider = ({children}: any) => {
  const {user} = useAuthUser();
  const [settings, setSettings] = useState(defaultSettingContext.settings);
  const [userSettings, setUserSettings] = useState(
    defaultSettingContext.userSettings,
  );
  const [loading, setLoading] = useState(defaultSettingContext.loading);

  useEffect(() => {
    fetchSettings();
  }, []);

  const makeApiKey = (length: number) => {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  };

  const updateSetting = (
    id: string | null,
    settingId: string,
    type: number,
    value: number | string | boolean | null,
    timeFrom: string | null = null,
    timeTo: string | null = null,
    dayFrom: number | null = null,
    dayTo: number | null = null,
    isRegularTradingHours: boolean | null = null,
    isExtendedTradingHours: boolean | null = null,
    isFlattenAll: boolean | null = null,
    timeRanges: object[] = []
  ) => {
    return jwtAxios.patch(`/user/settings/update`, {
      id,
      settingId,
      userId: user.id,
      type,
      value,
      timeFrom,
      timeTo,
      dayFrom,
      dayTo,
      isRegularTradingHours,
      isExtendedTradingHours,
      isFlattenAll,
      timeRanges
    });
  };

  const fetchSettings = () => {
    setLoading(true);
    Promise.all([
      getUserSettings().then(({data}) => setUserSettings(data.data)),
      getSettings().then(({data}) => setSettings(data.data)),
    ])
      .then(() => {
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const getSettings = async () => {
    return jwtAxios.get(`/setting/get-all`);
  };

  const getUserSettings = async () => {
    return jwtAxios.get(`/user/settings/get-all/${user.id}`);
  };

  return (
    <SettingContext.Provider
      value={{
        settings,
        userSettings,
        loading,
      }}>
      <SettingActionsContext.Provider
        value={{makeApiKey, updateSetting, fetchSettings}}>
        {children}
      </SettingActionsContext.Provider>
    </SettingContext.Provider>
  );
};

export default SettingProvider;
